import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Stack,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import QRCode from 'qrcode';

const QRCodeModal = ({
  open,
  onClose,
  salonUrl,
  salonName,
  onCopyToClipboard
}) => {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && salonUrl) {
      generateQRCode();
    }
  }, [open, salonUrl]);

  const generateQRCode = async () => {
    if (!salonUrl) return;
    
    setLoading(true);
    try {
      // Generate QR code as data URL directly
      const dataUrl = await QRCode.toDataURL(salonUrl, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M'
      });
      
      setQrCodeDataUrl(dataUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadQRCode = () => {
    if (qrCodeDataUrl) {
      const link = document.createElement('a');
      link.download = `qr-code-${salonName || 'salon'}.png`;
      link.href = qrCodeDataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const shareQRCode = async () => {
    if (navigator.share && qrCodeDataUrl) {
      try {
        // Convert data URL to blob
        const response = await fetch(qrCodeDataUrl);
        const blob = await response.blob();
        const file = new File([blob], `qr-code-${salonName || 'salon'}.png`, { type: 'image/png' });
        
        await navigator.share({
          title: `QR Code - ${salonName || 'Mon Salon'}`,
          text: `Scannez ce QR code pour découvrir ${salonName || 'mon salon'} et prendre rendez-vous !`,
          files: [file]
        });
      } catch (error) {
        console.error('Error sharing QR code:', error);
        // Fallback to copy URL
        onCopyToClipboard();
      }
    } else {
      // Fallback to copy URL
      onCopyToClipboard();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 1
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" component="div">
          QR Code de votre salon
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Partagez ce QR code avec vos clients pour qu'ils accèdent directement à votre salon
        </Typography>

        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          mb: 3,
          p: 2,
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          minHeight: 300,
          alignItems: 'center'
        }}>
          {loading ? (
            <Typography variant="body2" color="text.secondary">
              Génération du QR code...
            </Typography>
          ) : qrCodeDataUrl ? (
            <img
              src={qrCodeDataUrl}
              alt="QR Code"
              style={{
                maxWidth: '100%',
                height: 'auto',
                borderRadius: 8
              }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Erreur lors de la génération du QR code
            </Typography>
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {salonName && `${salonName} - `}Salon de beauté
        </Typography>

        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ 
            wordBreak: 'break-all',
            bgcolor: 'grey.50',
            p: 1,
            borderRadius: 1,
            display: 'block'
          }}
        >
          {salonUrl}
        </Typography>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={downloadQRCode}
            sx={{ flex: 1 }}
          >
            Télécharger
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<ShareIcon />}
            onClick={shareQRCode}
            sx={{ flex: 1 }}
          >
            Partager
          </Button>
          
          <Button
            variant="contained"
            startIcon={<CopyIcon />}
            onClick={onCopyToClipboard}
            sx={{ flex: 1 }}
          >
            Copier le lien
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default QRCodeModal;